"use client";

import React from "react";
import { PhoneCall, ArrowRight, ShieldAlert, Clock, MapPin, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const specs = [{
    label: "Response Time",
    value: "Under 60 Mins",
    icon: Clock
  }, {
    label: "Dispatch Coverage",
    value: "MD • DC • NoVA",
    icon: MapPin
  }, {
    label: "Crew Model",
    value: "100% Direct Technicians",
    icon: CheckCircle2
  }];
  return <section id="hero" data-nav="dark" className="relative min-h-[85vh] lg:min-h-[90vh] flex items-center justify-center overflow-hidden bg-background">
      {/* Background Image with Responsive Tags */}
      <div className="absolute inset-0 z-0 select-none">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/water_extraction_technician_residential_flood.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/water_extraction_technician_residential_flood.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/water_extraction_technician_residential_flood.png-wvc-sizes" alt="TriServe technician operating heavy water extraction unit on standing water" loading="eager" decoding="async" className="h-full w-full object-cover object-center" />
        {/* Multi-stop industrial scrim for guaranteed optical contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/80 to-black/95 backdrop-brightness-75" />
      </div>

      {/* Decorative Technical Grid Overlay */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-1 opacity-10 [background-image:linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] [background-size:4rem_4rem]" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="max-w-3xl">
          {/* Operational Status Pill */}
          <div className="inline-flex items-center gap-2.5 rounded-full border border-primary/40 bg-primary/20 px-3.5 py-1.5 backdrop-blur-md mb-6 sm:mb-8">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent" />
            </span>
            <span className="font-mono text-xs uppercase tracking-widest text-primary-foreground font-semibold">
              Live 24/7 Rapid Dispatch
            </span>
            <span className="text-white/40 text-xs">|</span>
            <span className="text-xs text-white/90 font-medium">
              MD • DC • Northern Virginia
            </span>
          </div>

          {/* Main Headline */}
          <h1 className="font-sans text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.08] mb-6">
            Water damage & flood restoration
          </h1>

          {/* Operational Subline & Reassuring Body Copy */}
          <p className="font-sans text-lg sm:text-xl text-white/95 font-medium leading-relaxed mb-4">
            Immediate extraction, industrial drying, and full repair for burst pipes, basement floods, and severe storm runoff.
          </p>

          <p className="font-sans text-sm sm:text-base text-white/80 leading-relaxed max-w-2xl mb-8 sm:mb-10">
            Our certified local crews dispatch directly with commercial extractors and desiccant dehumidifiers. We arrive fast, stop further structural damage, and work side-by-side with your insurance company from initial cleanup through final reconstruction.
          </p>

          {/* CTA Button Group */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-12">
            <Button asChild size="lg" className="h-13 px-6 rounded-md bg-primary text-primary-foreground font-bold hover:bg-primary/90 shadow-lg text-base transition-colors">
              <a href="tel:855-435-6631" className="inline-flex items-center justify-center gap-2.5">
                <PhoneCall className="size-5 shrink-0 text-white" />
                <span>Call Now</span>
                <span className="font-mono text-xs opacity-90 tracking-wider ml-1 hidden xs:inline">(855-435-6631)</span>
              </a>
            </Button>

            <Button asChild size="lg" className="h-13 px-6 rounded-md bg-accent text-accent-foreground font-extrabold hover:bg-accent/90 border border-amber-500/40 shadow-lg text-base transition-colors">
              <Link to="/ContactRequestServicePage" className="inline-flex items-center justify-center gap-2">
                <span>Request Service</span>
                <ArrowRight className="size-5 shrink-0" />
              </Link>
            </Button>
          </div>

          {/* Technical Spec Bar */}
          <div className="pt-6 border-t border-white/15 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {specs.map((spec, i) => {
            const IconComponent = spec.icon;
            return <div key={spec.label} data-index={i} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-md p-3 backdrop-blur-sm">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-white/10 text-accent">
                    <IconComponent className="size-4" />
                  </div>
                  <div>
                    <p className="text-xs text-white/60 font-medium">
                      {spec.label}
                    </p>
                    <p className="font-mono text-xs sm:text-sm font-bold text-white">
                      {spec.value}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>

      {/* Subtle Bottom Divider Strip */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-secondary" />
    </section>;
}